import { useEffect, useId, useState, type ReactNode } from 'react';

import { CATALOGS, LOCALES, useLocaleStore, useT } from '../../i18n';
import { Signing } from '../../platform/signing';
import { HelligkeitZeile } from '../helligkeit/HelligkeitZeile';
import { JE_PRUEFUNG, type Schluessel, type Wert } from '../../platform/einstellungen';
import {
  eintraegeDerGruppe,
  eintragsText,
  gruppenReihenfolge,
  gruppenText,
  inOberflaeche,
  zeichneEintrag,
  type Bindungen,
} from './einstellungsFelder';
import { verwirfQrUeberschreibungen } from '../../persistence/qrUeberschreibungen';
import { getStore } from '../../persistence/store';
import { Icon } from '../../ui/Icon';
import { mischeEinstellungenNeu } from '../../store/einstellungenMischen';
import { useAppStore, type Thema } from '../../store/useAppStore';
import { ACCENTS, PALETTES } from '../../theme/palettes';
import { Dialog } from '../../ui/Dialog';
import { istGeraet } from '../../platform/gleis';

import { BelegeGruppe } from './BelegeGruppe';
import { PasswortSetzen } from './PasswortSetzen';
import { RueckstandGruppe } from './RueckstandGruppe';
import { MitErklaerung } from './MitErklaerung';
import { ServerGroup } from './ServerGroup';
import { Flag } from '../../ui/Flag';
import { Switch as SharedSwitch } from '../../ui/Switch';
import styles from './Sheets.module.css';

/**
 * Die Einstellungen, die schon etwas bewirken: Erscheinungsbild und Bogen-Verhalten.
 * Die Nachbereitungs-Optionen aus dem Entwurf (Bildschirmfoto, Scanner, Puffer, Haptik)
 * kommen mit dem Absenden-Ablauf.
 */
export function SettingsDialog() {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  const closeSheet = useAppStore((s) => s.closeSheet);
  const thema = useAppStore((s) => s.thema);
  const setzeThema = useAppStore((s) => s.setzeThema);
  const accent = useAppStore((s) => s.accent);
  const setAccent = useAppStore((s) => s.setAccent);
  const halfPoints = useAppStore((s) => s.halfPoints);
  const toggleHalfPoints = useAppStore((s) => s.toggleHalfPoints);
  const timerShow = useAppStore((s) => s.timerShow);
  const setTimerShow = useAppStore((s) => s.setTimerShow);
  const videoMaxSeconds = useAppStore((s) => s.videoMaxSeconds);
  const setVideoMaxSeconds = useAppStore((s) => s.setVideoMaxSeconds);
  const archivTage = useAppStore((s) => s.archivTage);
  const setArchivTage = useAppStore((s) => s.setArchivTage);
  const afterSubmit = useAppStore((s) => s.afterSubmit);
  const stationComments = useAppStore((s) => s.stationComments);
  const arten = useAppStore((s) => s.kommentarArten);
  const toggleKommentarArt = useAppStore((s) => s.toggleKommentarArt);
  const setStationComments = useAppStore((s) => s.setStationComments);
  const fotosKopie = useAppStore((s) => s.fotosKopie);
  const spracheWaehlbar = useAppStore((s) => s.spracheWaehlbar);
  const setFotosKopie = useAppStore((s) => s.setFotosKopie);
  const medienBeschriftung = useAppStore((s) => s.medienBeschriftung);
  const setMedienBeschriftung = useAppStore((s) => s.setMedienBeschriftung);
  const quellen = useAppStore((s) => s.quellen);
  /**
   * Gibt eine Geräteverwaltung diese Einstellung vor?
   *
   * ⚠️ **Ohne diese Sperre sähe die App kaputt aus.** Der Prüfer änderte einen Wert, beim
   * nächsten Start stünde wieder der des MDM da, und nichts sagte warum. Gesperrt und
   * beschriftet ist ehrlicher als scheinbar bedienbar.
   */
  // Während einer Prüfung schlägt die App das MDM bei allem, was je Prüfung setzbar ist
  // (Entscheidung des Nutzers, 2026-10-10) — dort also keine Sperre.
  const examId = useAppStore((s) => s.examId);
  const vomMdm = (schluessel: Schluessel) =>
    quellen[schluessel] === 'mdm' && !(examId && JE_PRUEFUNG.has(schluessel));
  /**
   * ⚠️ **Ein gescannter Code schlägt seit dem 2026-09-13 auch das MDM** (Entscheidung des
   * Nutzers). Ohne einen Weg zurück bliebe ein einmal gescanntes iPad der Verwaltung dauerhaft
   * entzogen: das MDM dürfte weiter Werte schicken, käme aber nie wieder zum Zug.
   */
  const ausQr = Object.values(quellen).some((q) => q === 'qr');
  const deviceName = useAppStore((s) => s.deviceName);
  const setDeviceName = useAppStore((s) => s.setDeviceName);
  const [deviceId, setDeviceId] = useState('');

  // Die Kennung nur anzeigen — sie ist erzeugt, nicht einstellbar. Auf dem Web-Rückfall
  // gibt es sie nicht; dann bleibt das Feld leer statt eine zu erfinden.
  useEffect(() => {
    let aktuell = true;
    void Signing.getIdentity()
      .then((i) => {
        if (aktuell) setDeviceId(i.deviceId);
      })
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, []);
  const toggleAfterSubmit = useAppStore((s) => s.toggleAfterSubmit);
  const bogenAnzeige = useAppStore((s) => s.bogenAnzeige);
  const setzeBogenAnzeige = useAppStore((s) => s.setzeBogenAnzeige);
  const querformat = useAppStore((s) => s.querformat);
  const setzeQuerformat = useAppStore((s) => s.setzeQuerformat);

  /**
   * Was jede Einstellung gerade trägt und wie man sie ändert.
   *
   * ⚠️ **Die eine Stelle, an der Katalog und Store zusammenkommen.** Der Katalog sagt, *was* es
   * gibt und *ob* es erscheint; hier steht, *wohin* der Wert geht. Fehlt eine Bindung, zeichnet
   * `zeichneEintrag` nichts — besser eine Zeile weniger als eine, die beim Tippen nichts tut.
   */
  const bindungen: Bindungen = {
    ui_theme: { wert: thema, setze: (v) => setzeThema(v as Thema) },
    sheet_half_points: { wert: halfPoints, setze: () => toggleHalfPoints() },
    sheet_show_timer: { wert: timerShow, setze: (v) => setTimerShow(v === true) },
    station_comments_enabled: { wert: stationComments, setze: (v) => setStationComments(v === true) },
    // Die fünf Kommentararten (Auftrag des Nutzers, 2026-09-18). Sie hängen an einem Bündel im
    // Store, nicht an fünf Feldern — s. `kommentarArten` dort und `domain/kommentararten.ts`.
    text_comments: { wert: arten.text, setze: () => toggleKommentarArt('text') },
    sketch_comments: { wert: arten.handwriting, setze: () => toggleKommentarArt('handwriting') },
    audio_comments: { wert: arten.audio, setze: () => toggleKommentarArt('audio') },
    photo_comments: { wert: arten.photo, setze: () => toggleKommentarArt('photo') },
    video_comments: { wert: arten.video, setze: () => toggleKommentarArt('video') },
    video_max_seconds: { wert: videoMaxSeconds, setze: (v) => setVideoMaxSeconds(Number(v)) },
    deleted_files_retention_days: { wert: archivTage, setze: (v) => setArchivTage(Number(v)) },
    next_candidate_button: { wert: afterSubmit.scanner, setze: () => toggleAfterSubmit('scanner') },
    submit_haptics: { wert: afterSubmit.haptics, setze: () => toggleAfterSubmit('haptics') },
    save_photos_in_album: { wert: fotosKopie, setze: (v) => setFotosKopie(v === true) },
    media_label: { wert: medienBeschriftung, setze: (v) => setMedienBeschriftung(v === true) },
    // Die Schalter aus dem alten OSCE-Client (2026-10-02). `auto_upload` steht nur auf der
    // iPad-Seite und hat deshalb hier keine Bindung.
    colorful_text: { wert: bogenAnzeige.farbig, setze: (v) => setzeBogenAnzeige('farbig', v === true) },
    show_task_points: {
      wert: bogenAnzeige.aufgabenpunkte,
      setze: (v) => setzeBogenAnzeige('aufgabenpunkte', v === true),
    },
    show_sum_points: { wert: bogenAnzeige.summe, setze: (v) => setzeBogenAnzeige('summe', v === true) },
    editable_task_points: {
      wert: bogenAnzeige.aenderbar,
      setze: (v) => setzeBogenAnzeige('aenderbar', v === true),
    },
    actors_enabled: {
      wert: bogenAnzeige.schauspieler,
      setze: (v) => setzeBogenAnzeige('schauspieler', v === true),
    },
    landscape_mode: { wert: querformat, setze: (v) => setzeQuerformat(v === true) },
  };

  const bausteine = {
    Schalter: (p: { label: string; on: boolean; onChange: (n: boolean) => void; gesperrt?: boolean }) => (
      <Switch key={p.label} label={p.label} on={p.on} onChange={p.onChange} gesperrt={p.gesperrt} />
    ),
    Stufen: (p: {
      label: string;
      werte: { wert: Wert; text: string }[];
      aktuell: Wert;
      onChange: (n: Wert) => void;
      gesperrt?: boolean;
    }) => (
      <div key={p.label} className={styles.stufenBlock}>
        <div className={styles.textLabel}>{p.label}</div>
        <div className={styles.swatches}>
          {p.werte.map((w) => (
            <button
              key={String(w.wert)}
              type="button"
              className={`tap ${styles.stufe}`}
              data-on={p.aktuell === w.wert}
              aria-pressed={p.aktuell === w.wert}
              disabled={p.gesperrt}
              onClick={() => p.onChange(w.wert)}
            >
              {w.text}
            </button>
          ))}
        </div>
      </div>
    ),
  };

  /**
   * Die Sonderfälle: Einträge, die mehr tun als einen Wert zu setzen.
   *
   * ⚠️ **Sie folgen demselben `inOberflaeche`** wie alle anderen — wer die Sprachwahl aus der
   * App nimmt, nimmt sie wirklich heraus. Nur die **Aktionen** der Servergruppe bleiben
   * unabhängig davon stehen: „Beispielprüfungen laden" ist der einzige Weg zu Daten, wenn kein
   * Server erreichbar ist, und ein Gerät ohne jede Prüfung wäre sonst eine Sackgasse.
   */
  const sonderfall = (gruppe: string): ReactNode[] => {
    const teile: ReactNode[] = [];

    // Kein Wähler, wenn die Aufsicht die Sprache festlegt (`language_changeable`, s. `features/sprache.ts`).
    if (gruppe === 'ui' && inOberflaeche('language') && spracheWaehlbar) {
      teile.push(
        <MitErklaerung key="sprache" name="language" locale={locale}>
          <div className={styles.stufenBlock}>
            <div className={styles.textLabel}>{eintragsText('language', locale)}</div>
            <div className={styles.languages}>
              {LOCALES.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`tap ${styles.language}`}
                  data-on={locale === key}
                  aria-pressed={locale === key}
                  disabled={vomMdm('language')}
                  onClick={() => setLocale(key)}
                >
                  <Flag locale={key} />
                  <span>{CATALOGS[key].label}</span>
                </button>
              ))}
            </div>
            <div className={styles.languageNote}>
              {vomMdm('language') ? t.settings.managedByMdm : t.settings.languageNote}
            </div>
          </div>
        </MitErklaerung>,
      );
    }

    if (gruppe === 'ui' && inOberflaeche('ui_accent')) {
      teile.push(
        <MitErklaerung key="akzent" name="ui_accent" locale={locale}>
          <div className={styles.stufenBlock}>
            <div className={styles.textLabel}>{eintragsText('ui_accent', locale)}</div>
            <div className={styles.swatches}>
              {ACCENTS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`tap ${styles.swatch}`}
                  data-on={accent === key}
                  aria-label={PALETTES[key].label}
                  aria-pressed={accent === key}
                  disabled={vomMdm('ui_accent')}
                  style={{ background: PALETTES[key].base }}
                  onClick={() => setAccent(key)}
                />
              ))}
            </div>
          </div>
        </MitErklaerung>,
      );
    }

    // Seit 2026-10-02 auch in der App setzbar (`PasswortSetzen`).
    if (gruppe === 'lock' && inOberflaeche('settings_password')) {
      teile.push(
        <MitErklaerung key="passwort" name="settings_password" locale={locale}>
          <PasswortSetzen />
        </MitErklaerung>,
      );
    }

    if (gruppe === 'device' && inOberflaeche('device_name')) {
      teile.push(
        <MitErklaerung key="geraet" name="device_name" locale={locale}>
          <div className={styles.stufenBlock}>
            {/* Der Name geht in jede Beglaubigung ein — deshalb steht er hier und nicht nur
                im Betriebssystem: ab iOS 16 gibt Apple ihn fremden Apps nicht mehr heraus. */}
            <label className={styles.textRow}>
              <span className={styles.textLabel}>{eintragsText('device_name', locale)}</span>
              <input
                className={styles.textInput}
                value={deviceName}
                placeholder={t.settings.deviceNamePlaceholder}
                readOnly={vomMdm('device_name')}
                disabled={vomMdm('device_name')}
                onChange={(e) => setDeviceName(e.target.value)}
              />
            </label>
            <div className={styles.textNote}>
              {vomMdm('device_name') ? t.settings.managedByMdm : t.settings.deviceNameNote}
            </div>
            <div className={styles.textNote}>
              {t.settings.deviceId}: <code>{deviceId || '—'}</code>
            </div>
          </div>
        </MitErklaerung>,
      );
    }

    return teile;
  };

  return (
    <Dialog kicker={t.settings.kicker} title={t.settings.title} onClose={closeSheet} width={520}>
      {/*
        * ⚠️ **Derselbe Regler steht auch im `SperrDialog`** — mit Absicht zweimal gezeichnet
        * und nicht einmal darüber. Er muss **in** das Blatt: Radix setzt bei offenem Dialog
        * `pointer-events: none` auf den Body, und ausserhalb liegt er hinter dem Dunkelfeld.
        * Am 2026-09-17 zuerst über der Verzweigung in `App.tsx` versucht — im Browser lag er
        * unbedienbar am Seitenfuss. Die Entscheidung, **ob** gesperrt ist, bleibt dort.
        */}
      <HelligkeitZeile />
      <div className={`sc ${styles.settingsBody}`}>
        {/*
          * Gruppen und Einträge in der Reihenfolge des Katalogs (`settings/katalog.json`).
          * Eine Gruppe, aus der nichts übrig bleibt, erscheint gar nicht erst — sonst stünde
          * eine Überschrift über leerem Grund.
          */}
        {gruppenReihenfolge().map((gruppe) => {
          const eigene = sonderfall(gruppe);
          const zeilen = eintraegeDerGruppe(gruppe).map((eintrag) => {
            const zeile = zeichneEintrag(
              eintrag,
              bindungen[eintrag.name as Schluessel],
              locale,
              vomMdm(eintrag.name as Schluessel),
              bausteine,
            );
            return zeile ? (
              <MitErklaerung key={eintrag.name} name={eintrag.name} locale={locale}>
                {zeile}
              </MitErklaerung>
            ) : null;
          });
          const inhalt = [...eigene, ...zeilen].filter(Boolean);
          if (inhalt.length === 0) return null;

          // Seit 2026-10-02 keine Gruppen-Fussnote mehr: die Erklärung steht hinter dem „?"
          // jeder Einstellung (`MitErklaerung`).
          return (
            <Group key={gruppe} label={gruppenText(gruppe, locale)}>
              {inhalt}
            </Group>
          );
        })}

        {/* ⚠️ **Der Serverzugang bleibt handgebaut.** Er prüft die Anmeldung, holt den Katalog
            und lädt Beispielprüfungen; seine Eingabefelder folgen ihren eigenen Flags, die
            Aktionen bleiben erreichbar — ohne sie käme ein Gerät ohne Server an keine Daten. */}
        <ServerGroup Group={Group} />

        {/*
          ⚠️ **Nur im Browser.** Am iPad liegen Aufnahmen, Bogenabzüge und Beglaubigungen in
          Documents und gehen über Finder oder iTunes hinaus (`UIFileSharingEnabled`, seit
          2026-09-11). Im Browser sind sie in IndexedDB eingeschlossen — dort ist dieser Knopf
          der einzige Weg an sie heran. Auf dem Gerät wäre er ein zweiter Weg zu demselben
          Bestand, und der kleinere von beiden fiele am längsten nicht auf.
        */}
        {!istGeraet() && <BelegeGruppe Group={Group} />}

        {/* Nur sichtbar, wenn wirklich etwas aus einem Code stammt — sonst wäre es ein Knopf
            ohne Gegenstand. */}
        {ausQr && (
          <Group label={t.qr.kicker}>
            <button
              type="button"
              className={`tap ${styles.qrVerwerfen}`}
              onClick={() => {
                void (async () => {
                  const store = getStore();
                  const clientId = useAppStore.getState().clientId;
                  await verwirfQrUeberschreibungen(store, clientId);
                  await mischeEinstellungenNeu();
                })();
              }}
            >
              <Icon name="undo" size={15} />
              <span>{t.qr.verwerfen}</span>
            </button>
          </Group>
        )}

        {/* Ganz unten: was noch nicht übertragen ist, je Art (Vorgabe des Nutzers, 2026-10-02). */}
        <RueckstandGruppe Group={Group} />
      </div>
    </Dialog>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.group}>
      <div className={styles.groupLabel}>{label}</div>
      {children}
    </div>
  );
}

/**
 * Die Zeile um den Schalter: Beschriftung links, Schalter rechts. Das Bedienelement selbst
 * kommt aus `ui/Switch` — Verhalten und Optik eines Schalters leben in der ganzen App an einer
 * Stelle, hier steht nur die Zeile darum.
 */
function Switch({
  label,
  note,
  on,
  onChange,
  gesperrt,
}: {
  label: string;
  note?: string;
  on: boolean;
  onChange: (next: boolean) => void;
  /** Von einer Geräteverwaltung vorgegeben — anzeigen, aber nicht bedienbar. */
  gesperrt?: boolean;
}) {
  const id = useId();
  const t = useT();
  return (
    <div className={styles.switchRow} data-gesperrt={!!gesperrt}>
      <label className={styles.switchLabel} htmlFor={id}>
        {label}
        {note && <span className={styles.switchNote}>{note}</span>}
        {/* Der Vermerk steht bei der Zeile, nicht am Fuss der Gruppe: Wer den Schalter drücken
            will, soll dort lesen, warum er nicht geht. */}
        {gesperrt && <span className={styles.switchNote}>{t.settings.managedByMdm}</span>}
      </label>
      <SharedSwitch id={id} on={on} onChange={onChange} disabled={gesperrt} />
    </div>
  );
}
